import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import {
  FLAG_GETTING_DEPOSIT_MIGRATIONS,
  GET_DEPOSIT_MIGRATIONS,
  CLEAR_DEPOSIT_MIGRATIONS,
  FLAG_DEPOSIT_MIGRATION_ACTIVITY,
  GET_DEPOSIT_MIGRATION,
  CLEAR_DEPOSIT_MIGRATION,
  FLAG_GETTING_DEPOSIT_MIGRATION_MOVEMENTS,
  GET_DEPOSIT_MIGRATION_MOVEMENTS,
  CLEAR_DEPOSIT_MIGRATION_MOVEMENTS,
} from '../types';
import {
  DEPOSIT_MIGRATION_ENDPOINT,
  generateGetDepositMigrationEndpoint,
  generateDepositMigrationMovementEndpoint,
  generateReprocessDepositMigrationEndpoint,
} from '../../config/endpoints';
import {
  BASIC_POST_CONFIG,
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
} from '../../config/rest';
import { DEPOSIT_MIGRATION_PATH } from '../../config/paths';
import { DEPOSIT_MIGRATION_FILE_STATUS } from '../../config/constants';
import { isErrorResponse } from '../../utils/error-handlers';

const flagGettingDepositMigrations = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_DEPOSIT_MIGRATIONS,
    payload: flag,
  });

const flagDepositMigrationActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_DEPOSIT_MIGRATION_ACTIVITY,
    payload: flag,
  });

const flagGettingDepositMigrationMovements = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_DEPOSIT_MIGRATION_MOVEMENTS,
    payload: flag,
  });

const getDepositMigrations =
  (tableFilters = null) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingDepositMigrations(true));

      const query = tableFilters || {};

      const url = `${DEPOSIT_MIGRATION_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const depositMigrations = await response.json();

      dispatch({
        type: GET_DEPOSIT_MIGRATIONS,
        payload: depositMigrations,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingDepositMigrations(false));
    }
  };

const clearDepositMigrations = () => (dispatch) =>
  dispatch({
    type: CLEAR_DEPOSIT_MIGRATIONS,
  });

const getDepositMigration =
  ({ depositMigrationId }) =>
  async (dispatch) => {
    try {
      dispatch(flagDepositMigrationActivity(true));

      const url = generateGetDepositMigrationEndpoint(depositMigrationId);

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const depositMigration = await response.json();

      dispatch({
        type: GET_DEPOSIT_MIGRATION,
        payload: depositMigration,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagDepositMigrationActivity(false));
    }
  };

const clearDepositMigration = () => (dispatch) =>
  dispatch({
    type: CLEAR_DEPOSIT_MIGRATION,
  });

const postDepositMigration =
  ({ bankId, file }) =>
  async (dispatch) => {
    try {
      dispatch(flagDepositMigrationActivity(true));

      const url = `${DEPOSIT_MIGRATION_ENDPOINT}?${QueryString.stringify({
        bankId,
      })}`;

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(url, {
        ...BASIC_POST_CONFIG,
        body: formData,
      });

      await isErrorResponse(response, null, dispatch);

      const depositMigration = await response.json();

      if (
        depositMigration.status === DEPOSIT_MIGRATION_FILE_STATUS.FAILED.value
      ) {
        toastr.error(
          'Error',
          depositMigration.errorMessage ||
            'No se pudo procesar el estado de cuenta.',
        );
      } else if (
        depositMigration.status ===
        DEPOSIT_MIGRATION_FILE_STATUS.WITH_OBSERVATIONS.value
      ) {
        toastr.warning(
          'Carga con observaciones',
          'Quedaron movimientos sin match o con coincidencia parcial. Revísalos en el detalle.',
        );
      } else {
        toastr.success(
          'Éxito',
          'El estado de cuenta se procesó correctamente.',
        );
      }

      dispatch(push(`${DEPOSIT_MIGRATION_PATH}/${depositMigration.id}`));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagDepositMigrationActivity(false));
    }
  };

const postReprocessDepositMigration =
  ({ depositMigrationId }) =>
  async (dispatch) => {
    try {
      dispatch(flagDepositMigrationActivity(true));

      const url = generateReprocessDepositMigrationEndpoint(depositMigrationId);

      const response = await fetch(url, { ...DEFAULT_POST_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const depositMigration = await response.json();

      if (
        depositMigration.status === DEPOSIT_MIGRATION_FILE_STATUS.FAILED.value
      ) {
        toastr.error(
          'Error',
          depositMigration.errorMessage ||
            'No se pudo reprocesar el estado de cuenta.',
        );
      } else if (
        depositMigration.status ===
        DEPOSIT_MIGRATION_FILE_STATUS.WITH_OBSERVATIONS.value
      ) {
        toastr.warning(
          'Carga con observaciones',
          'Quedaron movimientos sin match o con coincidencia parcial. Revísalos en el detalle.',
        );
      } else {
        toastr.success('Éxito', 'La carga se reprocesó correctamente.');
      }

      dispatch(push(`${DEPOSIT_MIGRATION_PATH}/${depositMigration.id}`));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagDepositMigrationActivity(false));
    }
  };

const getDepositMigrationMovements =
  ({ depositMigrationId, ...tableFilters }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingDepositMigrationMovements(true));

      const url = `${generateDepositMigrationMovementEndpoint(
        depositMigrationId,
      )}?${QueryString.stringify(tableFilters)}`;

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const movements = await response.json();

      dispatch({
        type: GET_DEPOSIT_MIGRATION_MOVEMENTS,
        payload: movements,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingDepositMigrationMovements(false));
    }
  };

const clearDepositMigrationMovements = () => (dispatch) =>
  dispatch({
    type: CLEAR_DEPOSIT_MIGRATION_MOVEMENTS,
  });

export {
  getDepositMigrations,
  clearDepositMigrations,
  getDepositMigration,
  clearDepositMigration,
  postDepositMigration,
  postReprocessDepositMigration,
  getDepositMigrationMovements,
  clearDepositMigrationMovements,
};
